import Http from 'services/Http';
import { getOAuthTargetOrigin } from 'utils/webOrigin';

// Per-provider OAuth wiring. Keep UI concerns (icons, labels) out of here so
// this helper can be reused anywhere a cloud account needs (re)authentication.
const OAUTH = {
  google: {
    authUrl: '/v1/google-oauth/auth-url?scope=drive',
    windowName: 'google-oauth-drive',
    successType: 'GOOGLE_OAUTH_SUCCESS',
    errorType: 'GOOGLE_OAUTH_ERROR',
  },
  microsoft: {
    authUrl: '/v1/microsoft-oauth/auth-url',
    windowName: 'microsoft-oauth-drive',
    successType: 'MICROSOFT_OAUTH_SUCCESS',
    errorType: 'MICROSOFT_OAUTH_ERROR',
  },
};

/**
 * Open a provider OAuth popup and resolve with the new `tokenId` on success.
 * Rejects if the popup is blocked, the user cancels (closes the tab), or the
 * provider reports an error.
 *
 * @param {'google'|'microsoft'} provider
 * @returns {Promise<string>} the new OAuthToken id
 */
export default async function openCloudOAuth(provider) {
  const cfg = OAUTH[provider];
  if (!cfg) throw new Error(`Unknown cloud provider: ${provider}`);

  const { data } = await Http.get(cfg.authUrl);
  const authTab = window.open(data.authUrl, cfg.windowName);
  if (!authTab) throw new Error('Popup blocked');

  return new Promise((resolve, reject) => {
    // eslint-disable-next-line prefer-const -- assigned after handleMessage for hoisted cleanup
    let closedPoll;
    function cleanup() {
      window.removeEventListener('message', handleMessage);
      clearInterval(closedPoll);
    }
    function handleMessage(event) {
      if (event.source !== authTab) return;
      if (event.origin !== getOAuthTargetOrigin()) return;
      if (event.data.type === cfg.successType) {
        cleanup();
        resolve(event.data.tokenId);
      } else if (event.data.type === cfg.errorType) {
        cleanup();
        reject(new Error('OAuth failed'));
      }
    }
    // Treat a manually-closed popup as a cancel so callers can reset state.
    closedPoll = setInterval(() => {
      if (authTab.closed) {
        cleanup();
        reject(new Error('OAuth cancelled'));
      }
    }, 600);
    window.addEventListener('message', handleMessage);
    authTab.focus();
  });
}
